/**
 * Jyotish Tarang — AI Jyotish Q&A Page
 * Streaming responses | Chart-aware | Question templates
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import BuyCreditModal from '../components/BuyCreditModal';

interface Message { role: 'user' | 'assistant'; content: string; }
interface Chart   { id: number; name: string; }

/**
 * Dependency-free lightweight Markdown renderer, ported from the mobile
 * app's AskAIScreen.tsx (found + fixed there first, 6 Aug 2026, while
 * capturing a Play Store screenshot) after confirming this web page has
 * the identical live bug: the AI backend (report_generator/ai_api) writes
 * answers in Markdown (`## `/`### ` headings, `**bold**`, `---` rules,
 * `- ` bullets), but this page rendered `{msg.content}` as raw plain text
 * with `whiteSpace:'pre-wrap'`, leaking the literal syntax to real,
 * paying customers on every Ask AI answer. No markdown library is in
 * package.json, so this stays scoped to just what the backend emits
 * rather than pulling in a full Markdown engine for a chat bubble.
 */
function renderMarkdownLite(content: string): React.ReactNode {
  const renderInline = (text: string, keyPrefix: string): React.ReactNode => {
    const parts = text.split(/(\*\*.+?\*\*)/g).filter(p => p.length > 0);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>;
    });
  };
  return content.split('\n').map((line, i) => {
    const trimmed = line.trim();
    if (trimmed === '---' || trimmed === '') {
      return <div key={i} style={{ height: 8 }} />;
    }
    const headingMatch = trimmed.match(/^#{1,6}\s+(.*)$/);
    if (headingMatch) {
      return (
        <div key={i} style={{ fontWeight: 700, fontSize: '1.05em', marginTop: 6, marginBottom: 2 }}>
          {renderInline(headingMatch[1], `h${i}`)}
        </div>
      );
    }
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      return <div key={i}>{'• '}{renderInline(trimmed.slice(2), `b${i}`)}</div>;
    }
    return <div key={i}>{renderInline(line, `p${i}`)}</div>;
  });
}

export default function AIQAPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [charts,   setCharts]   = useState<Chart[]>([]);
  const [chartId,  setChartId]  = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [usage,    setUsage]    = useState<any>(null);
  const [sessionId]= useState(`sess_${Date.now()}`);
  const [showBuyAI, setShowBuyAI] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState('');
  const bottomRef  = useRef<HTMLDivElement>(null);
  const inputRef   = useRef<HTMLTextAreaElement>(null);

  const TEMPLATES: string[] = t('aiQa.templates', { returnObjects: true }) as unknown as string[];

  useEffect(() => {
    axios.get('/api/charts/').then(r => {
      const cl: Chart[] = r.data;
      setCharts(cl);
      if (cl.length > 0) setChartId(cl[0].id);
    });
    axios.get('/api/ai/usage').then(r => setUsage(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isLocked = user?.tier === 'free' && (usage?.lifetime_used || usage?.daily_used || 0) >= (usage?.free_lifetime || 1);

  async function sendMessage(question: string = input.trim()) {
    if (!question || loading || !chartId) return;
    // Phase 5 (14 Jul 2026, one-time-payments, decision 2): removed the old
    // silent no-op here that blocked Free-tier users from even attempting a
    // 2nd question once their lifetime freebie was used. Free tier can now
    // still buy a ₹99 one-time question -- let the request reach the
    // backend and handle the 429 (credit_type) response below instead.

    const userMsg: Message = { role: 'user', content: question };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // Add streaming assistant message
    const assistantIdx = messages.length + 1;
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      const resp = await fetch('/api/ai/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          question,
          chart_id:   chartId,
          session_id: sessionId,
          history:    messages.slice(-10),
        }),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        // Phase 5 (14 Jul 2026, one-time-payments): a 429 with credit_type
        // means the tier's AI quota was hit but a ₹99 credit purchase can
        // still unlock one more question -- offer BuyCreditModal instead of
        // a dead-end error bubble. See ai_qa.py::_check_rate_limit()'s
        // credit fallback (Phase 3, v4.2.2).
        if (errData.credit_type === 'ai_question') {
          setPendingQuestion(question);
          setShowBuyAI(true);
          // Drop the just-sent user question + empty placeholder bubble so
          // nothing dangling shows while the modal is open -- re-sent
          // automatically by handleAIPurchased() if they complete payment.
          setMessages(prev => prev.slice(0, -2));
          return;
        }
        throw new Error(errData.error || t('aiQa.errors.serverError', { status: resp.status }));
      }

      const data = await resp.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.answer) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant', content: data.answer
          };
          return updated;
        });
        if (data.done) {
          setUsage((u: any) => u ? {...u, daily_used: (u.daily_used||0)+1,
                                        hourly_used: (u.hourly_used||0)+1} : u);
        }
      }
    } catch (e: any) {
      const errMsg = e?.message && e.message !== 'undefined'
        ? e.message
        : t('aiQa.errors.somethingWrong');
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          content: errMsg
        };
        return updated;
      });
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }

  async function handleAIPurchased() {
    setShowBuyAI(false);
    if (pendingQuestion) {
      const q = pendingQuestion;
      setPendingQuestion('');
      await sendMessage(q);
    }
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>

      {/* Top bar */}
      <div style={{
        background: 'var(--sfll)', borderBottom: '0.5px solid var(--parch2)',
        padding: '10px 20px', display: 'flex', alignItems: 'center',
        gap: 12, flexShrink: 0,
      }}>
        <span style={{ fontSize:20 }}>🔮</span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:500, color:'var(--indigo)' }}>
            {t('aiQa.title')}
          </div>
          <div style={{ fontSize:11, color:'var(--ink3)' }}>
            {t('aiQa.subtitle')}
          </div>
        </div>
        {charts.length > 1 && (
          <select
            value={chartId || ''}
            onChange={e => setChartId(Number(e.target.value))}
            className="form-input" style={{ width:'auto', padding:'6px 10px', fontSize:12 }}>
            {charts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        {usage && (
          <span style={{ fontSize:11, color:'var(--ink3)', whiteSpace:'nowrap' }}>
            {user?.tier==='free'
              ? `${usage.daily_used || 0}/${usage.limit || t('aiQa.oneLifetime')}`
              : `${usage.daily_used || 0} ${usage.limit || ''}`
            }
          </span>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex:1, overflowY:'auto', padding:'20px 20px 0' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>

          {messages.length === 0 && (
            <div>
              <div style={{ textAlign:'center', padding:'32px 0 24px',
                            color:'var(--ink3)' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🪐</div>
                <h3 style={{ color:'var(--indigo)', marginBottom:8 }}>
                  {t('aiQa.askQuestion')}
                </h3>
                <p style={{ fontSize:13, maxWidth:480, margin:'0 auto' }}>
                  {t('aiQa.askQuestionBody')}
                </p>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:20 }}>
                {TEMPLATES.map(tmpl => (
                  <button key={tmpl}
                          onClick={() => sendMessage(tmpl)}
                          disabled={loading || isLocked}
                          style={{
                            textAlign:'left', padding:'10px 14px',
                            background:'var(--cream)', border:'0.5px solid var(--parch2)',
                            borderRadius:8, fontSize:12.5, color:'var(--ink)',
                            cursor:'pointer', transition:'all 0.15s', lineHeight:1.5,
                          }}
                          onMouseEnter={e => (e.currentTarget.style.borderColor='var(--sfb)')}
                          onMouseLeave={e => (e.currentTarget.style.borderColor='var(--parch2)')}>
                    {tmpl}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} style={{ marginBottom:16 }}>
              {msg.role === 'user' ? (
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  <div className="chat-msg user">{msg.content}</div>
                </div>
              ) : (
                <div style={{ display:'flex', gap:10 }}>
                  <div style={{
                    width:32, height:32, borderRadius:'50%',
                    background:'var(--sfb)', flexShrink:0, marginTop:4,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:16,
                  }}>🪐</div>
                  <div>
                    {msg.content ? (
                      <div className="chat-msg assistant">
                        {renderMarkdownLite(msg.content)}
                      </div>
                    ) : (
                      <div className="chat-typing">{t('aiQa.consultingChart')}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef}/>
        </div>
      </div>

      {/* Input area */}
      <div style={{
        borderTop: '0.5px solid var(--parch2)', background: 'var(--cream)',
        padding: '12px 20px', flexShrink: 0,
      }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          {/* Phase 5 (14 Jul 2026, one-time-payments): this used to fully
              replace the input with an "Upgrade to Gold" wall once the free
              lifetime question was used, blocking Free tier from ever
              reaching AI Q&A again. Decision 2 says ₹99 pay-per-use must be
              open to everyone including Free -- so the hint below is
              informational only; sending still works and offers
              BuyCreditModal if the backend returns credit_type. */}
          {isLocked && (
            <div className="info-box info-saffron" style={{ marginBottom: 8, fontSize: 12 }}>
              {t('aiQa.usedFreeQuestion')}{' '}
              <a href="/pricing">{t('aiQa.upgradeToGold')}</a> {t('aiQa.forTenPerMonth')}
            </div>
          )}
          <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={t('aiQa.inputPlaceholder') as string}
              className="form-input"
              rows={2}
              style={{ flex:1, resize:'none', lineHeight:1.5 }}
              disabled={loading}
            />
            <button
              className="btn btn-primary"
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              style={{ flexShrink:0, alignSelf:'stretch' }}>
              {loading ? '...' : t('aiQa.sendButton')}
            </button>
          </div>
          <p style={{ fontSize:10.5, color:'var(--ink3)', marginTop:6, marginBottom:0 }}>
            {t('aiQa.disclaimer')}
          </p>
        </div>
      </div>

      <BuyCreditModal open={showBuyAI} creditType="ai_question"
                      onClose={() => { setShowBuyAI(false); setPendingQuestion(''); }}
                      onPurchased={handleAIPurchased}/>
    </div>
  );
}
